import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaperPlane,
    faTrash,
    faEnvelopeOpen,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import { MessageThread } from "./MessagesPage";
import "./Messages.css";

/** Contact form submissions logged from the public homepage. */
const SubmissionsList = () => {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        api.contactSubmissions()
            .then((res) => setSubs(res.submissions?.data || []))
            .finally(() => setLoading(false));
    }, []);

    const open = (sub) => {
        setExpanded((prev) => (prev === sub.id ? null : sub.id));
        if (!sub.read_at) {
            api.markSubmissionRead(sub.id).catch(() => {});
            setSubs((prev) =>
                prev.map((s) =>
                    s.id === sub.id
                        ? { ...s, read_at: new Date().toISOString() }
                        : s,
                ),
            );
        }
    };

    const remove = async (sub) => {
        if (!window.confirm(`Delete submission from ${sub.name}?`)) return;
        await api.deleteSubmission(sub.id);
        setSubs((prev) => prev.filter((s) => s.id !== sub.id));
    };

    if (loading) return <p className="messages__empty">Loading…</p>;
    if (subs.length === 0)
        return (
            <p className="messages__empty">
                No contact form submissions yet. Everything sent through the
                homepage form appears here (and is emailed to you).
            </p>
        );

    return (
        <div className="submissions">
            {subs.map((sub) => (
                <div
                    key={sub.id}
                    className={`card submissions__item ${!sub.read_at ? "submissions__item--unread" : ""}`}
                >
                    <button
                        className="submissions__head"
                        onClick={() => open(sub)}
                    >
                        <div className="submissions__who">
                            {!sub.read_at && (
                                <span className="submissions__dot" />
                            )}
                            <strong>{sub.name}</strong>
                            <span className="submissions__email">
                                {sub.email}
                            </span>
                        </div>
                        <div className="submissions__meta">
                            <span className="submissions__service">
                                {sub.service}
                            </span>
                            <span className="submissions__date">
                                {new Date(sub.created_at).toLocaleString(
                                    "en-CA",
                                    {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                    },
                                )}
                            </span>
                        </div>
                    </button>
                    {expanded === sub.id && (
                        <div className="submissions__body">
                            <div className="submissions__detail-grid">
                                {sub.company && (
                                    <span>
                                        <em>Company:</em> {sub.company}
                                    </span>
                                )}
                                {sub.timeline && (
                                    <span>
                                        <em>Timeline:</em> {sub.timeline}
                                    </span>
                                )}
                            </div>
                            <p>{sub.message}</p>
                            <div className="submissions__actions">
                                <a
                                    className="btn btn--primary btn--sm"
                                    href={`mailto:${sub.email}?subject=Re: your inquiry at kyle-street.com`}
                                >
                                    <FontAwesomeIcon icon={faEnvelopeOpen} />{" "}
                                    Reply by Email
                                </a>
                                <button
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => remove(sub)}
                                >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

/** Admin inbox: client thread list + selected conversation + reply box. */
const AdminMessages = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get("client");
    const tab = searchParams.get("tab") || "messages";
    const [contactUnread, setContactUnread] = useState(0);

    useEffect(() => {
        api.unreadMessages()
            .then((res) => setContactUnread(Number(res.contact_count || 0)))
            .catch(() => {});
    }, [tab]);

    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const loadThreads = useCallback(() => {
        api.messageThreads()
            .then((res) => setThreads(res.threads || []))
            .finally(() => setLoadingThreads(false));
    }, []);

    useEffect(() => {
        loadThreads();
    }, [loadThreads]);

    useEffect(() => {
        if (!selectedId) return;
        setLoadingThread(true);
        api.messages(selectedId)
            .then((res) => setMessages(res.messages || []))
            .finally(() => setLoadingThread(false));
        api.markMessagesRead(selectedId)
            .then(() => loadThreads())
            .catch(() => {});
    }, [selectedId, loadThreads]);

    const reply = async (e) => {
        e.preventDefault();
        if (!body.trim() || !selectedId) return;
        setSending(true);
        setError("");
        try {
            const res = await api.sendMessage({
                client_id: Number(selectedId),
                body: body.trim(),
            });
            setMessages((prev) => [...prev, res.message]);
            setBody("");
            loadThreads();
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="messages">
            <div className="section-header">
                <h2>Messages</h2>
            </div>

            <div className="messages__tabs">
                <button
                    className={`messages__tab ${tab === "messages" ? "messages__tab--active" : ""}`}
                    onClick={() => setSearchParams({ tab: "messages" })}
                >
                    Client Messages
                </button>
                <button
                    className={`messages__tab ${tab === "submissions" ? "messages__tab--active" : ""}`}
                    onClick={() => setSearchParams({ tab: "submissions" })}
                >
                    Form Submissions
                    {contactUnread > 0 && (
                        <span className="messages__unread">
                            {contactUnread}
                        </span>
                    )}
                </button>
            </div>

            {tab === "submissions" ? (
                <SubmissionsList />
            ) : (
                <div className="messages__layout">
                    <div className="card messages__threads">
                        {loadingThreads ? (
                            <p className="messages__empty">Loading…</p>
                        ) : threads.length === 0 ? (
                            <p className="messages__empty">
                                No client messages yet.
                            </p>
                        ) : (
                            threads.map((th) => (
                                <button
                                    key={th.client.id}
                                    className={`messages__thread-item ${
                                        String(th.client.id) === selectedId
                                            ? "messages__thread-item--active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setSearchParams({
                                            client: String(th.client.id),
                                        })
                                    }
                                >
                                    <div className="messages__thread-top">
                                        <strong>{th.client.name}</strong>
                                        {th.unread > 0 && (
                                            <span className="messages__unread">
                                                {th.unread}
                                            </span>
                                        )}
                                    </div>
                                    <span className="messages__thread-preview">
                                        {th.last_message.sender?.role ===
                                        "admin"
                                            ? "You: "
                                            : ""}
                                        {th.last_message.body.slice(0, 60)}
                                        {th.last_message.body.length > 60
                                            ? "…"
                                            : ""}
                                    </span>
                                    <span className="messages__thread-date">
                                        {new Date(
                                            th.last_message.created_at,
                                        ).toLocaleDateString("en-CA")}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="card messages__panel">
                        {!selectedId ? (
                            <p className="messages__empty">
                                Select a conversation.
                            </p>
                        ) : loadingThread ? (
                            <p className="messages__empty">Loading…</p>
                        ) : (
                            <>
                                <MessageThread
                                    messages={messages}
                                    meId={user?.id}
                                />
                                <form
                                    className="messages__composer"
                                    onSubmit={reply}
                                >
                                    {error && (
                                        <div className="error-message">
                                            {error}
                                        </div>
                                    )}
                                    <div className="messages__composer-row">
                                        <textarea
                                            value={body}
                                            onChange={(e) =>
                                                setBody(e.target.value)
                                            }
                                            placeholder="Reply… (the client is emailed a notification)"
                                            rows={3}
                                            required
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn--primary"
                                            disabled={sending || !body.trim()}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPaperPlane}
                                            />{" "}
                                            {sending ? "Sending…" : "Send"}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMessages;
